import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { accord, pluriel } from '@/lib/pluriel';
import { AdminAvatar } from '@/components/admin/avatar';
import { AdminButton } from '@/components/admin/button';
import { Card } from '@/components/admin/card';
import { ChipFilter } from '@/components/admin/chip-filter';
import { AdminIcon } from '@/components/admin/icon';
import { PageHead } from '@/components/admin/page-head';
import { Pagination } from '@/components/admin/pagination';
import { Tag } from '@/components/admin/tag';
import { BulkStatusModal } from '@/components/admin/bulk-status-modal';
import { useConfirm, useToast } from '@/components/ui/feedback';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import { fmtFCFA } from '@/lib/format';
import { ORDER_STATUS_SUB_LABEL, PAYMENT_METHOD_LABEL } from '@/lib/orders/labels';
import { ordersApi } from '@/lib/orders/orders-api';
import {
  useArchiveOrder,
  useBulkStatus,
  useDeleteOrder,
  useOrdersList,
  useOrdersRealtime,
  useUnarchiveOrder,
} from '@/lib/orders/orders-hooks';
import type { CustomerLite, OrderStatus, OrdersFilter } from '@/lib/orders/types';
import { statusOrderV2 } from '@/lib/status';

const TABS: Array<[string, string, OrderStatus[] | null]> = [
  ['all', 'Toutes', null],
  ['todo', 'À traiter', ['PENDING_PAYMENT', 'PAID', 'CONFIRMED']],
  ['hub', 'Au hub / prêt', ['IN_HUB', 'READY_TO_SHIP']],
  ['ship', 'En livraison', ['SHIPPING']],
  ['delivered', 'Livrées', ['DELIVERED']],
  ['cancelled', 'Annulées', ['CANCELLED']],
  ['disputed', 'Litiges', ['DISPUTED']],
  ['archived', 'Archivées', null],
];

const PAGE_SIZE = 25;

interface RowMenu {
  id: string;
  top: number;
  left: number;
}

export function AdminOrdersPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [avancementOuvert, setAvancementOuvert] = useState(false);
  /**
   * Filtres transversaux, indépendants des onglets de statut.
   *
   * Une commande peut être « à traiter » ET impayée ET passée sans compte :
   * ces trois questions ne se répondent pas au même endroit du parcours, et
   * les mêler aux onglets aurait multiplié ces derniers sans les rendre plus
   * clairs.
   */
  const [filtreExtra, setFiltreExtra] = useState<
    "aucun" | "impayees" | "virements" | "invites" | "entreprises"
  >("aucun");
  const [menu, setMenu] = useState<RowMenu | null>(null);

  const isArchivedView = tab === 'archived';

  const filter: OrdersFilter = useMemo(() => {
    const f: OrdersFilter = { take: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
    const tabDef = TABS.find((t) => t[0] === tab);
    if (tab === 'archived') f.archived = true;
    else if (tabDef && tabDef[2]) f.status = tabDef[2];
    if (search.trim().length > 0) f.customerQuery = search.trim();
    if (filtreExtra === "impayees") f.paymentStatus = ["UNPAID"];
    // Les virements en attente forment la file de travail quotidienne : le
    // stock qu'ils réservent se libère tout seul au bout de cinq jours.
    if (filtreExtra === "virements") {
      f.paymentMethod = ["BANK_TRANSFER"];
      f.paymentStatus = ["UNPAID"];
    }
    if (filtreExtra === "invites") f.guestOnly = true;
    if (filtreExtra === "entreprises") f.customerType = ["b2b"];
    return f;
  }, [tab, page, search, filtreExtra]);

  const { data, isLoading, refetch } = useOrdersList(filter);
  const { data: stats } = useOrdersRealtime();
  const bulkStatus = useBulkStatus();
  const archiveMut = useArchiveOrder();
  const unarchiveMut = useUnarchiveOrder();
  const deleteMut = useDeleteOrder();
  const confirm = useConfirm();
  const toast = useToast();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = items.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = (page - 1) * PAGE_SIZE + items.length;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exportCsv = () => {
    const url = ordersApi.exportCsvUrl(filter);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commandes-${Date.now()}.csv`;
    a.click();
  };

  const batchBordereaux = async () => {
    const ids = [...selected];
    if (ids.length === 0) return alert('Sélectionnez au moins une commande.');
    const res = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1'}/admin/orders/bulk/waybill.pdf`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${(window as unknown as { __token?: string }).__token ?? ''}`,
        },
        body: JSON.stringify({ ids }),
      },
    );
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  // L'ancienne version demandait de TAPER le code du statut dans une fenêtre
  // système, sans liste ni contrôle : une faute de frappe échouait en silence.
  const onBulkAdvance = () => {
    if (selected.size === 0) {
      toast.info('Sélectionnez au moins une commande.');
      return;
    }
    setAvancementOuvert(true);
  };

  const appliquerAvancement = async (status: string) => {
    const ids = [...selected];
    await bulkStatus.mutateAsync({ ids, status, reasonCode: 'BULK_ACTION' });
    toast.success(`${pluriel(ids.length, 'commande')} ${accord(ids.length, 'mise')} à jour.`);
    setSelected(new Set());
    await refetch();
  };

  const onBulkArchive = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const action = isArchivedView ? unarchiveMut : archiveMut;
    await Promise.all(ids.map((id) => action.mutateAsync(id)));
    setSelected(new Set());
    await refetch();
  };

  const onBulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Supprimer ${pluriel(ids.length, 'commande')}`,
      message: (
        <>Suppression réversible (récupérable en base). Confirmez en tapant le mot ci-dessous.</>
      ),
      requireText: 'SUPPRIMER',
      confirmLabel: 'Supprimer',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await Promise.all(ids.map((id) => deleteMut.mutateAsync(id)));
      toast.success(`${pluriel(ids.length, 'commande')} ${accord(ids.length, 'supprimée')}.`);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Suppression impossible'));
    }
    setSelected(new Set());
    await refetch();
  };

  const openMenu = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenu({ id, top: r.bottom + 4, left: Math.max(8, r.right - 190) });
  };
  const closeMenu = () => setMenu(null);

  const doArchive = async (id: string) => {
    closeMenu();
    await (isArchivedView ? unarchiveMut : archiveMut).mutateAsync(id);
    await refetch();
  };
  const doDelete = async (id: string) => {
    closeMenu();
    const order = items.find((o) => o.id === id);
    const num = order?.number ?? '';
    const ok = await confirm({
      title: 'Supprimer la commande',
      message: (
        <>
          Suppression réversible (récupérable en base). Tapez le numéro de commande pour confirmer.
        </>
      ),
      requireText: num || undefined,
      confirmLabel: 'Supprimer',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteMut.mutateAsync(id);
      toast.success(`Commande ${num} supprimée.`);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Suppression impossible'));
    }
    await refetch();
  };

  return (
    <>
      <PageHead
        title="Commandes"
        sub={`${pluriel(total, 'commande')} ${accord(total, 'trouvée')} · actualisé toutes les 30 s`}
        actions={
          <>
            <AdminButton variant="ghost" onClick={exportCsv}>
              <AdminIcon n="download" s={14} />
              Exporter CSV
            </AdminButton>
            <AdminButton variant="ghost" onClick={batchBordereaux} data-testid="bulk-bordereaux">
              <AdminIcon n="print" s={14} />
              Imprimer bons
            </AdminButton>
            <AdminButton variant="primary" onClick={() => navigate('/commandes/nouvelle')}>
              <AdminIcon n="plus" s={14} />
              Nouvelle commande
            </AdminButton>
          </>
        }
      />

      {/* `alerte` n'est vrai que pour un retard réel : une pastille de couleur
          allumée en permanence cesse d'être lue. Même règle qu'en page
          d'accueil, pour que les deux écrans se lisent pareil. */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {(
          [
            ['À traiter', stats?.byStatus.PENDING_PAYMENT ?? 0, 'délai d’une heure', false],
            ['Payées', stats?.byStatus.PAID ?? 0, 'à confirmer', false],
            ['En tournée', stats?.byStatus.SHIPPING ?? 0, 'chez les livreurs', false],
            // Affichait le nombre de commandes CRÉÉES : une commande passée il y
            // a cinq minutes comptait comme livrée.
            [
              'Livrées aujourd’hui',
              stats?.deliveredTodayCount ?? 0,
              fmtFCFA(stats?.deliveredTodayRevenue ?? 0),
              false,
            ],
            [
              'En retard',
              stats?.slaLate ?? 0,
              'à débloquer',
              (stats?.slaLate ?? 0) > 0,
            ],
          ] as Array<[string, number, string, boolean]>
        ).map(([l, v, f, alerte]) => (
          <div key={l} className={alerte ? 'kpi warning' : 'kpi'}>
            <div className="kpi-l">{l}</div>
            <div className="kpi-v tn">{v}</div>
            <div className="kpi-foot muted">{f}</div>
          </div>
        ))}
      </div>

      <Card>
        <div className="toolbar">
          <div className="filter-bar" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
            {TABS.map(([k, l, statuses]) => {
              const count =
                k === 'archived'
                  ? (stats?.archived ?? 0)
                  : statuses
                    ? statuses.reduce((acc, s) => acc + (stats?.byStatus[s] ?? 0), 0)
                    : total;
              return (
                <ChipFilter
                  key={k}
                  on={tab === k}
                  count={count}
                  onClick={() => {
                    setTab(k);
                    setPage(1);
                    setSelected(new Set());
                  }}
                  data-testid={`tab-${k}`}
                >
                  {l}
                </ChipFilter>
              );
            })}
          </div>

          {/*
            Séparés des onglets de statut : ces filtres répondent à d'autres
            questions (qui paie, qui commande) et se combinent avec eux.
          */}
          <div
            className="filter-bar"
            style={{ marginBottom: 0, flexWrap: 'wrap', marginLeft: 'auto' }}
          >
            {(
              [
                ['impayees', 'Impayées'],
                ['virements', 'Virements en attente'],
                ['invites', 'Sans compte'],
                ['entreprises', 'Entreprises'],
              ] as const
            ).map(([cle, libelle]) => (
              <ChipFilter
                key={cle}
                on={filtreExtra === cle}
                onClick={() => {
                  // Un second clic retire le filtre : sans cela, il faut
                  // recharger la page pour revenir à la liste entière.
                  setFiltreExtra((actuel) => (actuel === cle ? 'aucun' : cle));
                  setPage(1);
                  setSelected(new Set());
                }}
                data-testid={`filtre-${cle}`}
              >
                {libelle}
              </ChipFilter>
            ))}
          </div>
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <input
              type="search"
              placeholder="Rechercher client / numéro…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              data-testid="orders-search"
              style={{
                padding: '6px 10px',
                border: '1px solid var(--a-border)',
                borderRadius: 6,
                minWidth: 220,
              }}
            />
            {selected.size > 0 && (
              <>
                {!isArchivedView && (
                  <AdminButton variant="ghost" onClick={onBulkAdvance}>
                    Avancer ({selected.size})
                  </AdminButton>
                )}
                <AdminButton variant="ghost" onClick={onBulkArchive}>
                  <AdminIcon n="box" s={14} />
                  {isArchivedView ? 'Désarchiver' : 'Archiver'} ({selected.size})
                </AdminButton>
                <AdminButton variant="ghost" onClick={onBulkDelete}>
                  <AdminIcon n="close" s={14} />
                  Supprimer ({selected.size})
                </AdminButton>
              </>
            )}
          </div>
        </div>

        <div className="table-wrap" style={{ margin: 0, overflowX: 'auto' }}>
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }} className="muted">
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div
              style={{ padding: 40, textAlign: 'center' }}
              className="muted"
              data-testid="orders-empty"
            >
              {isArchivedView
                ? 'Aucune commande archivée.'
                : 'Aucune commande trouvée pour ces filtres.'}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 24 }}>
                    <input
                      type="checkbox"
                      checked={selected.size === items.length && items.length > 0}
                      onChange={(e) =>
                        setSelected(e.target.checked ? new Set(items.map((o) => o.id)) : new Set())
                      }
                    />
                  </th>
                  <th>N° / Date</th>
                  <th>Client</th>
                  <th>Ville</th>
                  <th>Articles</th>
                  <th>Paiement</th>
                  <th className="right">Total</th>
                  <th>Statut</th>
                  <th>Étape</th>
                  <th>Hub</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => {
                  const s = statusOrderV2(o.status);
                  const customerName = customerLabel(o.customer);
                  return (
                    <tr
                      key={o.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/commandes/${o.id}`)}
                      data-testid={`order-row-${o.number}`}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(o.id)}
                          onChange={() => toggleSelect(o.id)}
                        />
                      </td>
                      <td>
                        <span className="mono">{o.number}</span>
                        <div className="muted" style={{ fontSize: 11 }}>
                          {new Date(o.createdAt).toLocaleString('fr-FR')}
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <AdminAvatar name={customerName} />
                          <span style={{ minWidth: 0 }}>
                            {customerName}
                            {/*
                              Visible dès la liste : une commande sans compte se
                              relance au téléphone, une commande d'entreprise
                              exige une facture avec ses mentions légales. Le
                              savoir après avoir ouvert la fiche fait perdre le
                              temps qu'on cherchait à gagner.
                            */}
                            {o.customer?.status === 'guest' && (
                              <span
                                className="muted"
                                style={{ marginLeft: 6, fontSize: 11 }}
                              >
                                sans compte
                              </span>
                            )}
                            {o.customer?.type === 'b2b' && (
                              <span
                                className="muted"
                                style={{ marginLeft: 6, fontSize: 11 }}
                              >
                                entreprise
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td>{o.shippingAddress?.city ?? '—'}</td>
                      <td className="tn">{o.lineCount ?? o.lines?.length ?? '—'}</td>
                      <td>
                        <Tag variant="neutral" noDot>
                          {o.paymentMethod ? PAYMENT_METHOD_LABEL[o.paymentMethod] : 'Non défini'}
                        </Tag>
                      </td>
                      <td className="right tn">
                        <strong>{fmtFCFA(o.totalAmount)}</strong>
                      </td>
                      <td>
                        <Tag variant={s.cls}>{s.l}</Tag>
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {ORDER_STATUS_SUB_LABEL[o.status]}
                      </td>
                      <td>
                        <span className="mono">{o.hub?.code ?? '—'}</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          aria-label="Actions"
                          data-testid={`order-actions-${o.number}`}
                          onClick={(e) => openMenu(e, o.id)}
                          style={{ padding: 4 }}
                        >
                          <AdminIcon n="dots" s={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {items.length > 0 && (
          <Pagination from={from} to={to} total={total} current={page} totalPages={totalPages} />
        )}
      </Card>

      {menu && (
        <>
          <div onClick={closeMenu} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div
            role="menu"
            style={{
              position: 'fixed',
              top: menu.top,
              left: menu.left,
              width: 190,
              background: 'var(--a-surface)',
              border: '1px solid var(--a-border)',
              borderRadius: 8,
              boxShadow: '0 8px 24px -6px rgba(0,0,0,0.18)',
              zIndex: 41,
              padding: 4,
            }}
          >
            <button
              type="button"
              className="row-menu-item"
              onClick={() => {
                closeMenu();
                navigate(`/commandes/${menu.id}`);
              }}
              style={menuItemStyle()}
            >
              <AdminIcon n="eye" s={14} />
              Voir le détail
            </button>
            <button
              type="button"
              className="row-menu-item"
              onClick={() => doArchive(menu.id)}
              style={menuItemStyle()}
              data-testid="menu-archive"
            >
              <AdminIcon n="box" s={14} />
              {isArchivedView ? 'Désarchiver' : 'Archiver'}
            </button>
            <button
              type="button"
              className="row-menu-item"
              onClick={() => doDelete(menu.id)}
              style={menuItemStyle(true)}
              data-testid="menu-delete"
            >
              <AdminIcon n="close" s={14} />
              Supprimer
            </button>
          </div>
        </>
      )}

      {avancementOuvert && (
        <BulkStatusModal
          count={selected.size}
          onClose={() => setAvancementOuvert(false)}
          onConfirm={appliquerAvancement}
        />
      )}
    </>
  );
}

/** Libellé client robuste : displayName → nom complet → téléphone → email → « Client ». */
function customerLabel(c?: CustomerLite | null): string {
  if (!c) return '—';
  const full = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return c.displayName?.trim() || full || c.phone || c.email || 'Client';
}

function menuItemStyle(danger = false): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    borderRadius: 6,
    fontSize: 13,
    background: 'transparent',
    cursor: 'pointer',
    color: danger ? 'var(--a-danger)' : 'var(--a-ink)',
  };
}
